/** Demand divided by places offered, from figures already stored on an allocation table. */

const NOT_DEMAND = /not applications|not the number who applied|places offered, not/i;
const PREF_RANK = /^(1st|2nd|3rd|4th|5th|6th)$/i;
const EXPLICIT_DEMAND = /^(all preferences|total preferences|applications?|applicants?)$/i;
const SEAT = /^(offers?|places allocated)$/i;

function parseCount(value) {
  if (value == null) return null;
  const text = String(value).trim().replace(/,/g, "");
  if (!/^\d+(?:\.\d+)?$/.test(text)) return null;
  return Number(text);
}

function columnIndex(columns, pattern) {
  return columns.findIndex((column) => pattern.test(column));
}

function yearFromText(value) {
  const match = String(value || "").match(/\b(20\d{2})\b/);
  return match ? Number(match[1]) : null;
}

function demandMeta(header) {
  if (/^all preferences$/i.test(header)) return { kind: "all", demandLabel: "preferences" };
  if (/^total preferences$/i.test(header)) return { kind: "all", demandLabel: "preferences" };
  if (/^applicants?$/i.test(header)) return { kind: "applicants", demandLabel: "applicants" };
  return { kind: "applications", demandLabel: "applications" };
}

function seatLabelFor(header) {
  return /^places allocated$/i.test(header) ? "places" : "offers";
}

function pushFactor(out, item) {
  if (item.applications == null || item.offers == null || item.offers <= 0 || item.applications <= 0) return;
  out.push(item);
}

function factorsFromTable(table) {
  const columns = (table.columns || []).map((column) => String(column || "").trim());
  const rows = Array.isArray(table.rows) ? table.rows : [];
  const caption = `${table.title || ""} ${table.caption || ""}`;
  if (NOT_DEMAND.test(caption)) return [];
  const out = [];

  const applied = [];
  const offered = [];
  for (const row of rows) {
    const label = String(row?.[0] || "").trim();
    const appliedMatch = label.match(/^Applied\s+(20\d{2})$/i);
    const offeredMatch = label.match(/^Offered\s+(20\d{2})$/i);
    if (appliedMatch) applied.push({ year: Number(appliedMatch[1]), row });
    if (offeredMatch) offered.push({ year: Number(offeredMatch[1]), row });
  }
  if (applied.length && offered.length) {
    const totalIndex = columnIndex(columns, /^total$/i);
    const index = totalIndex >= 0 ? totalIndex : columns.length - 1;
    for (const application of applied) {
      const offer = offered.find((row) => row.year === application.year);
      if (!offer) continue;
      pushFactor(out, {
        year: application.year,
        applications: parseCount(application.row[index]),
        offers: parseCount(offer.row[index]),
        kind: "applications",
        demandLabel: "applications",
        seatLabel: "offers",
      });
    }
    return out;
  }

  const yearRows = rows.filter((row) => /^20\d{2}$/.test(String(row?.[0] || "").trim()));
  if (yearRows.length) {
    const explicit = columns
      .map((column, index) => ({ column, index }))
      .filter((column) => column.index > 0 && EXPLICIT_DEMAND.test(column.column));
    const ranks = columns
      .map((column, index) => ({ column, index }))
      .filter((column) => column.index > 0 && PREF_RANK.test(column.column));
    const seats = columns
      .map((column, index) => ({ column, index }))
      .filter((column) => column.index > 0 && SEAT.test(column.column));
    if (!seats.length) return [];
    const seat = seats.find((column) => /^offers?$/i.test(column.column)) || seats[0];
    const seatLabel = seatLabelFor(seat.column);
    for (const row of yearRows) {
      const offers = parseCount(row[seat.index]);
      if (explicit.length) {
        const chosen = explicit[0];
        const count = parseCount(row[chosen.index]);
        if (count != null) {
          const meta = demandMeta(chosen.column);
          pushFactor(out, {
            year: Number(row[0]),
            applications: count,
            offers,
            seatLabel,
            ...meta,
          });
          continue;
        }
      }
      const values = ranks.map((column) => parseCount(row[column.index])).filter((value) => value != null);
      if (!values.length) continue;
      const rankNames = ranks.map((column) => column.column.toLowerCase());
      pushFactor(out, {
        year: Number(row[0]),
        applications: values.reduce((sum, value) => sum + value, 0),
        offers,
        kind: "ranked",
        demandLabel: rankNames.length === 3 && rankNames.join(",") === "1st,2nd,3rd"
          ? "1st, 2nd and 3rd preferences"
          : "preferences",
        seatLabel,
      });
    }
    return out;
  }

  const applicationIndex = columnIndex(columns, /^applications?$/i);
  const seatIndex = columns.findIndex((column, index) => index > 0 && SEAT.test(column));
  if (applicationIndex >= 0 && seatIndex > 0) {
    const total = rows.find((row) => /^total$/i.test(String(row?.[0] || "").trim()));
    const chosen = total || (rows.length === 1 ? rows[0] : null);
    if (!chosen) return [];
    pushFactor(out, {
      year: yearFromText(table.title) || yearFromText(table.caption),
      applications: parseCount(chosen[applicationIndex]),
      offers: parseCount(chosen[seatIndex]),
      kind: "applications",
      demandLabel: "applications",
      seatLabel: seatLabelFor(columns[seatIndex]),
    });
  }
  return out;
}

const KIND_RANK = { applications: 4, applicants: 4, all: 3, ranked: 2 };
const FIRST_EXAM_SAT = /^sat stage 1$/i;
const FIRST_EXAM_STAGE2 = /^reached stage 2$/i;
const FIRST_EXAM_PLACES = /^published places$/i;

function partitionTables(raw) {
  if (!Array.isArray(raw)) return [];
  return raw.filter(
    (item) => item && item.kind === "table" && Array.isArray(item.columns) && Array.isArray(item.rows)
  );
}

/** Latest year first. One figure per year, from the most direct published pair. */
export function oversubscriptionFromHistory(historyRows) {
  const byYear = new Map();
  const undated = [];
  for (const row of Array.isArray(historyRows) ? historyRows : []) {
    for (const table of partitionTables(row?.line_items)) {
      for (const factor of factorsFromTable(table)) {
        if (factor.year == null) {
          undated.push(factor);
          continue;
        }
        const current = byYear.get(factor.year);
        if (!current || (KIND_RANK[factor.kind] || 0) > (KIND_RANK[current.kind] || 0)) {
          byYear.set(factor.year, factor);
        }
      }
    }
  }
  return [...byYear.values()]
    .sort((a, b) => b.year - a.year)
    .slice(0, 5)
    .concat(undated.slice(0, 1));
}

export function formatOversubscriptionFactor(applications, offers) {
  if (!offers) return "";
  const text = (Math.round((applications / offers) * 10) / 10).toFixed(1);
  return `${text}X`;
}

/** School’s own count of who sat the first paper. Not offer-day preferences. */
export function firstExamFromHistory(historyRows) {
  const byYear = new Map();
  for (const row of Array.isArray(historyRows) ? historyRows : []) {
    for (const table of partitionTables(row?.line_items)) {
      if (table.role !== "first_exam") continue;
      const columns = (table.columns || []).map((column) => String(column || "").trim());
      const satIndex = columnIndex(columns, FIRST_EXAM_SAT);
      const stage2Index = columnIndex(columns, FIRST_EXAM_STAGE2);
      const placesIndex = columnIndex(columns, FIRST_EXAM_PLACES);
      const detailIndex = columnIndex(columns, /^detail$/i);
      if (satIndex < 0) continue;
      for (const record of Array.isArray(table.rows) ? table.rows : []) {
        if (!/^20\d{2}$/.test(String(record?.[0] || "").trim())) continue;
        const satStage1 = parseCount(record[satIndex]);
        const places = placesIndex >= 0 ? parseCount(record[placesIndex]) : null;
        if (satStage1 == null || satStage1 <= 0) continue;
        if (places != null && places <= 0) continue;
        byYear.set(Number(record[0]), {
          year: Number(record[0]),
          satStage1,
          reachedStage2: stage2Index >= 0 ? parseCount(record[stage2Index]) : null,
          places,
          detail: detailIndex >= 0 ? String(record[detailIndex] || "").trim() : "",
          satLabel: table.sat_label || "the first exam",
          clearedLabel: table.cleared_label || "reached stage 2",
          verb: table.verb || "sat",
          gate: table.gate || null,
          source: table.source || null,
          sourceLabel: table.source_label || "School exam figures",
        });
      }
    }
  }
  return [...byYear.values()].sort((a, b) => b.year - a.year).slice(0, 5);
}

export function oversubscriptionSentence(item) {
  const applications = Number(item.applications).toLocaleString("en-GB");
  const offers = Number(item.offers).toLocaleString("en-GB");
  return `${applications} ${item.demandLabel} for ${offers} ${item.seatLabel}`;
}

export function firstExamSentence(exam) {
  const sat = Number(exam.satStage1).toLocaleString("en-GB");
  const places = exam.places != null ? Number(exam.places).toLocaleString("en-GB") : null;
  const verb = exam.verb || "sat";
  const label = exam.satLabel || "the first exam";
  let text;
  if (verb === "applications") {
    text = places ? `${sat} applications were made for ${places} places.` : `${sat} applications were made.`;
  } else if (verb === "registered") {
    text = `${sat} registered for the entrance test.`;
  } else if (places) {
    text = `${sat} sat ${label} for ${places} places.`;
  } else {
    text = `${sat} sat ${label}.`;
  }
  if (exam.reachedStage2 != null) {
    text += ` ${Number(exam.reachedStage2).toLocaleString("en-GB")} ${exam.clearedLabel || "reached stage 2"}.`;
  }
  if (exam.detail) text += ` ${exam.detail}`;
  return text;
}

export function oversubscriptionNote(items, { twoStage = false, examGate = null, examHasPlaces = false } = {}) {
  const kinds = new Set(items.map((item) => item.kind));
  let base;
  if (kinds.has("ranked") && kinds.has("all")) {
    base = "Where All preferences is published, it includes 4th and later preferences. Otherwise 1st, 2nd and 3rd are added. Either figure is divided by the offers.";
  } else if (kinds.has("ranked")) {
    base = "1st, 2nd and 3rd preferences are added, then divided by the offers in the same table. Later preferences are not in that table.";
  } else if (kinds.has("all") && (kinds.has("applications") || kinds.has("applicants"))) {
    base = "Each year uses the applications or preferences published for that year, divided by the places offered in the same table.";
  } else if (kinds.has("all")) {
    base = "Preferences count every time the school was named, including 4th and later preferences. The factor divides that number by the places offered.";
  } else {
    base = "Applications divided by the places offered in the same published table.";
  }
  if (examGate === "shared") {
    return `${base} The exam line is who sat the shared first exam. It is not divided by one school's offers. Children who do not pass are not considered.`;
  }
  if (examGate === "standard") {
    return `${base} Children below the school's standard cannot be considered. The exam line is from the school's own publication, and it is not the offer-day preference count.`;
  }
  if (examGate === "applications") {
    return `${base} The exam line is applications for this school's entrance test, beside the places on that page. Children not invited to the second stage are not eligible.`;
  }
  if (examGate === "stage") {
    return examHasPlaces
      ? `${base} This school has a second stage. The exam line is who sat its first paper, divided by the places in that same publication. It is not the offer-day preference count.`
      : `${base} This school has a second stage. The exam line is who sat the first paper. Children who do not clear it are not considered.`;
  }
  if (twoStage) {
    return `${base} This school has a second stage. This factor counts names on the offer-day form. It is not how many children sat the first exam.`;
  }
  return base;
}
